// src/pages/PostPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';        // permite HTML no markdown
import rehypeSanitize from 'rehype-sanitize'; // sanitiza HTML
import { getPublicPostBySlug } from '../api/client';
import { useLanguage } from '../contexts/LanguageContext';

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&h=630&fit=crop&crop=entropy&auto=format';

export default function PostPage() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const formatDate = (iso) => {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(t('locale'), { year: 'numeric', month: 'long', day: 'numeric' });
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        const data = await getPublicPostBySlug(slug);
        if (mounted) setPost(data);
      } catch (e) {
        console.error(e);
        if (mounted) setErr('Post não encontrado ou não publicado.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  const dateISO = useMemo(() => (post?.published_at || post?.created_at), [post]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-64 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
        </div>
      </div>
    );
  }

  if (err || !post) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Link to="/blog" className="inline-flex items-center text-primary hover:opacity-80 mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('blog.backToList', 'Voltar aos posts')}
        </Link>
        <p className="text-gray-600">{err || 'Post não encontrado.'}</p>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto p-6">
      {/* breadcrumb simples */}
      <div className="text-sm text-gray-500 mb-3">
        <Link to="/" className="hover:underline">Home</Link> <span className="mx-1">/</span>
        <Link to="/blog" className="hover:underline">Blog</Link> <span className="mx-1">/</span>
        <span className="text-gray-700">{post.title}</span>
      </div>

      <h1 className="text-3xl md:text-4xl font-bold mb-3">{post.title}</h1>

      <div className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center"><Calendar className="w-4 h-4 mr-1" /> {formatDate(dateISO)}</span>
        <span className="inline-flex items-center"><Clock className="w-4 h-4 mr-1" /> {post.read_time_minutes} min</span>
        {post.author && <span>• {post.author}</span>}
      </div>

      {/* imagem de capa */}
      <img
        src={post.image_url || PLACEHOLDER_IMG}
        alt={post.title}
        className="w-full rounded-xl mb-6"
        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
      />

      {/* conteúdo markdown */}
      <div className="prose prose-neutral max-w-none">
        <ReactMarkdown
          // Se seu conteúdo NÃO usa HTML, remova rehypeRaw/rehypeSanitize
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeSanitize]}
        >
          {post.content || ''}
        </ReactMarkdown>
      </div>

      {/* tags */}
      {!!post.tags?.length && (
        <div className="mt-8 flex flex-wrap gap-2">
          {post.tags.map((tag, i) => (
            <span key={i} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">#{tag}</span>
          ))}
        </div>)}

      <div className="mt-10">
        <Link to="/blog" className="inline-flex items-center text-primary hover:opacity-80">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('blog.backToList', 'Voltar aos posts')}
        </Link>
      </div>
    </article>
  );
}
